/** Balance page — outstanding balance per student. */

import React from 'react';
import { usePortalBalance, type PortalStudentBalance } from '../api/portal';
import { BALANCE_OVERDUE_COLOUR, BALANCE_CREDIT_COLOUR } from '../config/ui';

function formatAmount(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(Math.abs(amount) / 100);
}

const BalanceCard: React.FC<{ student: PortalStudentBalance }> = ({ student }) => {
  const isOverdue = student.balance > 0;
  const isCredit  = student.balance < 0;
  const colour    = isOverdue ? BALANCE_OVERDUE_COLOUR : (isCredit ? BALANCE_CREDIT_COLOUR : '#6b7280');
  const label     = isOverdue ? 'Outstanding' : (isCredit ? 'Credit' : 'Settled');

  return (
    <div
      style={{
        background: '#fff',
        border: `1px solid ${colour}44`,
        borderLeft: `4px solid ${colour}`,
        borderRadius: '0.5rem',
        padding: '1rem 1.25rem',
        marginBottom: '0.75rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
      data-testid={`candeza-portal-balance-row-${student.student_id}`}
    >
      <div>
        <strong data-testid="candeza-portal-balance-student-name">
          {student.first_name} {student.last_name}
        </strong>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontWeight: 700, fontSize: '1.125rem', color: colour }}>
          {formatAmount(student.balance, student.currency)}
        </div>
        <div style={{ fontSize: '0.75rem', color: colour }}>{label}</div>
      </div>
    </div>
  );
};

const BalancePage: React.FC = () => {
  const { data, isLoading, isError } = usePortalBalance();
  const students = data?.students ?? [];

  if (isLoading) {
    return <div data-testid="candeza-portal-balance-loading">Loading balance…</div>;
  }
  if (isError) {
    return (
      <div data-testid="candeza-portal-balance-error" style={{ color: '#ef4444' }}>
        Could not load balance.
      </div>
    );
  }

  return (
    <div data-testid="candeza-portal-balance-page">
      <h1 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '1.25rem' }}>
        Account Balance
      </h1>
      {students.length === 0 ? (
        <p data-testid="candeza-portal-balance-empty" style={{ color: '#6b7280' }}>
          No balance information available.
        </p>
      ) : (
        <div data-testid="candeza-portal-balance-list">
          {students.map(s => <BalanceCard key={s.student_id} student={s} />)}
        </div>
      )}
    </div>
  );
};

export default BalancePage;

